import { z } from 'zod';
import { BatteryRemainingSchema } from '../types/Battery';
import { logger } from '../utils/logger';

export const HubBindIsOkSchema = z.object({
    imei: z.string().nonempty()
});

export type HubBindIsOkMessage = z.infer<typeof HubBindIsOkSchema>;

export const HubSetOnlineSchema = z.object({
    imei: z.string(),
    isOnline: z.boolean()
});

export type HubSetOnlineMessage = z.infer<typeof HubSetOnlineSchema>;

export const HubHbStatusSchema = z.object({
    imei: z.string(),
    isArm: z.boolean(),
    mode: z.int(),
    type: z.int()
});

export type HubHbStatusMessage = z.infer<typeof HubHbStatusSchema>;

export const HubMapPositionSchema = z.object({
    imei: z.string(),
    lat: z.number(),
    lng: z.number(),
    yaw: z.number(),
    isOnline: z.boolean()
});

export type HubMapPositionMessage = z.infer<typeof HubMapPositionSchema>;

export const HubFirmwareProgressSchema = z.object({
    imei: z.string(),
    progress: z.int()
});

export type HubFirmwareProgressMessage = z.infer<typeof HubFirmwareProgressSchema>;

export const HubBaudRateSchema = z.object({
    imei: z.string(),
    baudRate: z.string()
});

export type HubBaudRateMessage = z.infer<typeof HubBaudRateSchema>;

const HubBatteryListSchema = z.preprocess(
    (val) => {
        if (val === null || val === undefined) {
            return [];
        }
        if (Array.isArray(val)) {
            return val;
        }
        // Сервер кладёт в BatteryRemaining объект Batterys: { battery: [...] } —
        // список батарей завёрнут в обёртку. Раньше обёртка принималась за
        // одну батарею, не проходила валидацию и заряд тихо становился [].
        if (typeof val === 'object' && 'battery' in val) {
            const inner = (val as { battery: unknown }).battery;
            return Array.isArray(inner) ? inner : inner == null ? [] : [inner];
        }
        // Одну батарею сервер может прислать объектом. Всё, что не объект
        // (число, строка), батареей не является — заворачивать его бессмысленно:
        // получится элемент с undefined во всех полях.
        return typeof val === 'object' ? [val] : [];
    },
    // .catch() — самое важное здесь. Раньше одна кривая батарея
    // отбраковывала СООБЩЕНИЕ ЦЕЛИКОМ — вместе с высотой, скоростью
    // и спутниками, которые были корректны. Теперь цена — прочерк в
    // заряде, а не потеря всей телеметрии.
    //
    // Логируем причину: раньше .catch([]) съедал ошибку молча, и
    // несовпадение формата батареи с сервером было не отличить от
    // «телеметрии по батарее правда нет» — оба давали пустой список.
    z.array(BatteryRemainingSchema).catch((ctx) => {
        logger.error('batteryRemaining: неожиданный формат', ctx.error, ctx.value);
        return [];
    })
);

export const HubTelemetrySchema = z.object({
    imei: z.string(),
    batteryRemaining: HubBatteryListSchema,
    height: z.number(),
    speed: z.number(),
    sputniksCount: z.int()
});

export type HubTelemetryMessage = z.infer<typeof HubTelemetrySchema>;

export const HubStatusTextSchema = z.object({
    imei: z.string(),
    message: z.string(),
    severity: z.int(),
    time: z.string()
});

export type HubStatusTextMessage = z.infer<typeof HubStatusTextSchema>;
